// The accountant's own postings (06 §6.3–6.5): exchange rates typed or imported by finance, manual
// (adjustment) journals and their reversals, the month-end revaluation of foreign-currency balances
// and the year-end close of the P&L into retained earnings. Pure: integer minor units and decimal
// text rates, exact rational maths, every builder returns lines that balance or throws.
import {
  AccountingError,
  assertBalanced,
  getAccount,
  isKnownAccount,
  type Dimensions,
  type JournalLineDraft,
} from './accounting';
import { pnlSectionOf } from './finance-reports';
import { convert, isCurrencyCode, money, type CurrencyCode } from './money';

// ------------------------------------------------------------------- FX rates

/**
 * The group's base currency (14 §14.2). Finance keys every day's rates against it: one document per
 * day and currency, `fxRates/{date}__AED__{currency}`, "1 AED buys `rate` of the currency". Readers
 * accept either direction and cross two AED rates for a pair that has no rate of its own.
 */
export const FX_BASE_CURRENCY = 'AED' as const;
/** The currencies finance quotes against AED every day (14 §14.2, 12: India and the US dollar bills). */
export const FX_QUOTED_CURRENCIES = ['SAR', 'QAR', 'PHP', 'INR', 'USD'] as const;
export type FxQuotedCurrency = (typeof FX_QUOTED_CURRENCIES)[number];

const RATE_SCALE = 12;
const RATE_ONE = 10n ** BigInt(RATE_SCALE);

/**
 * A rate as a person types it: digits with at most 12 decimals, more than zero. Returns it tidied
 * (`"015.70"` → `"15.7"`), or undefined when it is not a usable rate. Commas are not accepted, so a
 * thousands separator can never be read as a decimal point.
 */
export function parseRateText(text: string): string | undefined {
  const m = /^(\d{1,9})(?:\.(\d{1,12}))?$/.exec(text.trim());
  if (!m) return undefined;
  const whole = (m[1] as string).replace(/^0+(?=\d)/, '');
  const fraction = (m[2] ?? '').replace(/0+$/, '');
  if (/^0+$/.test(whole) && fraction === '') return undefined;
  return fraction === '' ? whole : `${whole}.${fraction}`;
}

function rateValue(rate: string): bigint {
  const tidy = parseRateText(rate);
  if (tidy === undefined) throw new AccountingError(`"${rate}" is not an exchange rate`);
  const [whole, fraction = ''] = tidy.split('.');
  return BigInt(`${whole}${fraction.padEnd(RATE_SCALE, '0')}`);
}

function rateText(value: bigint): string {
  const whole = value / RATE_ONE;
  const fraction = (value % RATE_ONE).toString().padStart(RATE_SCALE, '0').replace(/0+$/, '');
  return fraction === '' ? `${whole}` : `${whole}.${fraction}`;
}

/** a × b to 12 decimals, rounded half-up: two rates through the base currency. */
export function multiplyRates(a: string, b: string): string {
  const product = rateValue(a) * rateValue(b);
  const rounded = (2n * product + RATE_ONE) / (2n * RATE_ONE);
  if (rounded <= 0n) throw new AccountingError('The cross rate is too small to keep');
  return rateText(rounded);
}

/** How far a new rate is from the one before, in basis points (1% = 100). For the "check it" flag. */
export function rateChangeBp(previous: string, next: string): number {
  const p = rateValue(previous);
  const n = rateValue(next);
  const diff = n > p ? n - p : p - n;
  return Number((diff * 10_000n * 2n + p) / (2n * p));
}

/** A change this large against the day before is flagged on the screen and in the history. */
export const LARGE_RATE_CHANGE_BP = 500;

export interface RateImportRow {
  readonly date: string;
  readonly currency: string;
  readonly rate: string;
}

/**
 * Rows pasted or read from a file: `date, currency, rate` per line (a header line is skipped, and so
 * are empty lines). The rate is "1 AED buys this much". Problems are named by line number; a line
 * with a problem is left out, never guessed.
 */
export function parseRateImport(text: string): {
  rows: RateImportRow[];
  problems: { line: number; why: string }[];
} {
  const rows: RateImportRow[] = [];
  const problems: { line: number; why: string }[] = [];
  const seen = new Set<string>();
  text.split(/\r?\n/).forEach((raw, i) => {
    const line = i + 1;
    if (raw.trim() === '') return;
    const cells = raw.split(/[,;\t]/).map((c) => c.trim().replace(/^"|"$/g, ''));
    if (i === 0 && /date/i.test(cells[0] ?? '')) return;
    const [date = '', currencyRaw = '', rateRaw = ''] = cells;
    const currency = currencyRaw.toUpperCase();
    if (!/^\d{4}-\d{2}-\d{2}$/.test(date) || Number.isNaN(Date.parse(`${date}T00:00:00Z`))) {
      problems.push({ line, why: `"${date}" is not a date like 2026-10-20` });
      return;
    }
    if (!(FX_QUOTED_CURRENCIES as readonly string[]).includes(currency)) {
      problems.push({
        line,
        why: `"${currencyRaw}" is not one of ${FX_QUOTED_CURRENCIES.join(', ')}`,
      });
      return;
    }
    const rate = parseRateText(rateRaw);
    if (rate === undefined) {
      problems.push({ line, why: `"${rateRaw}" is not a rate (digits and a dot, more than 0)` });
      return;
    }
    const key = `${date} ${currency}`;
    if (seen.has(key)) {
      problems.push({ line, why: `${currency} on ${date} is in the file twice` });
      return;
    }
    seen.add(key);
    rows.push({ date, currency, rate });
  });
  return { rows, problems };
}

// ------------------------------------------------------------ manual journals

/** Accounts that are wallets (14 §14.5): a line on them names the wallet, so balances stay one number. */
export const WALLET_ACCOUNTS: ReadonlySet<string> = new Set([
  '1000',
  '1001',
  '1005',
  '1010',
  '1015',
  '1020',
]);
/** Intercompany accounts name the other country (06 §6.5), so the pairs can be matched and eliminated. */
export const INTERCOMPANY_ACCOUNTS: ReadonlySet<string> = new Set(['1200', '2100']);
/** Accounts a manual journal may never touch: the translation reserve exists only in group figures. */
export const MANUAL_FORBIDDEN_ACCOUNTS: ReadonlySet<string> = new Set(['3300']);
export const MANUAL_JOURNAL_MAX_LINES = 40;

/** What a manual line may be linked to, and the journal dimension each one fills (06 §6.2). */
export const MANUAL_LINK_DIMENSION = {
  customer: 'customerId',
  shipment: 'shipmentId',
  supplier: 'supplierId',
  partner: 'partnerId',
  bill: 'billId',
  branch: 'branchId',
  container: 'containerId',
  trip: 'tripId',
  handover: 'handoverId',
  counter_entity: 'counterEntityId',
} as const;
export type ManualLinkKind = keyof typeof MANUAL_LINK_DIMENSION;

export interface ManualLineInput {
  readonly accountCode: string;
  readonly debitMinor: number;
  readonly creditMinor: number;
  readonly description: string;
  /** Required on a wallet account (1000, 1010, …), refused on any other. */
  readonly walletId?: string | undefined;
  readonly link?: { readonly kind: ManualLinkKind; readonly id: string } | undefined;
}

/**
 * The lines of a manual journal (`postAdjustment`, 04 §4.6), checked: 2 to 40 lines, each a known
 * account with one positive side, debits equal to credits, a wallet named on every wallet account,
 * the other country named on 1200 and 2100, and never 3300. The description and the link become
 * dimensions, so the general ledger shows them and reports can slice by the link.
 */
export function manualJournalLines(
  lines: readonly ManualLineInput[],
  entityId: string,
): JournalLineDraft[] {
  if (lines.length < 2) throw new AccountingError('A journal needs at least two lines');
  if (lines.length > MANUAL_JOURNAL_MAX_LINES) {
    throw new AccountingError(`A journal has at most ${MANUAL_JOURNAL_MAX_LINES} lines`);
  }
  const out = lines.map((l, i): JournalLineDraft => {
    const n = i + 1;
    if (!isKnownAccount(l.accountCode)) {
      throw new AccountingError(`Line ${n}: there is no account ${l.accountCode}`);
    }
    if (MANUAL_FORBIDDEN_ACCOUNTS.has(l.accountCode)) {
      throw new AccountingError(
        `Line ${n}: ${l.accountCode} is worked out by the group report and cannot be posted to`,
      );
    }
    const wallet = WALLET_ACCOUNTS.has(l.accountCode);
    if (wallet && !l.walletId) {
      throw new AccountingError(
        `Line ${n}: account ${l.accountCode} is money in a wallet, so name the wallet`,
      );
    }
    if (!wallet && l.walletId) {
      throw new AccountingError(`Line ${n}: account ${l.accountCode} is not a wallet account`);
    }
    if (INTERCOMPANY_ACCOUNTS.has(l.accountCode)) {
      if (l.link?.kind !== 'counter_entity') {
        throw new AccountingError(
          `Line ${n}: account ${l.accountCode} is between countries, so name the other country`,
        );
      }
      if (l.link.id === entityId) {
        throw new AccountingError(`Line ${n}: a country cannot owe itself`);
      }
    }
    const dimensions: Record<string, string> = {};
    const description = l.description.trim();
    if (description) dimensions['description'] = description;
    if (l.walletId) dimensions['walletId'] = l.walletId;
    if (l.link) dimensions[MANUAL_LINK_DIMENSION[l.link.kind]] = l.link.id;
    return {
      accountCode: l.accountCode,
      debitMinor: l.debitMinor,
      creditMinor: l.creditMinor,
      dimensions,
    };
  });
  assertBalanced(out);
  return out;
}

/** The debit side of a journal: what a manual journal moves, for the approval and the list. */
export const journalSizeMinor = (lines: readonly Pick<JournalLineDraft, 'debitMinor'>[]): number =>
  lines.reduce((s, l) => s + l.debitMinor, 0);

/**
 * The exact mirror of a journal: every debit becomes a credit and back, dimensions kept. A reversal
 * plus the original nets every account and wallet to zero (06 §6.1 rule 3).
 */
export function reversalLines(lines: readonly JournalLineDraft[]): JournalLineDraft[] {
  const out = lines.map((l) => ({
    accountCode: l.accountCode,
    debitMinor: l.creditMinor,
    creditMinor: l.debitMinor,
    dimensions: { ...l.dimensions },
  }));
  assertBalanced(out);
  return out;
}

// ------------------------------------------------------------ year-end close

interface Pair {
  debit: number;
  credit: number;
}

/** True for an account the year-end close empties: revenue, cost of sales, operating costs, other. */
export const isPnlAccount = (code: string): boolean => pnlSectionOf(code) !== undefined;

/** Retained earnings: where the year's result goes (06 §6.2). */
export const RETAINED_EARNINGS_ACCOUNT = '3200';

/**
 * The year-end closing journal (06 §6.3 "Period close … Year end: P&L → 3200"): every P&L account is
 * brought to zero for the year and the result lands on 3200. A profit credits 3200, a loss debits it.
 * `netResultMinor` is the profit (credit − debit of the P&L accounts). No movement, no lines.
 */
export function yearEndClosingLines(
  yearMovement: ReadonlyMap<string, Pair>,
  dimensions: Dimensions,
): { lines: JournalLineDraft[]; netResultMinor: number } {
  const lines: JournalLineDraft[] = [];
  let profit = 0;
  for (const code of [...yearMovement.keys()].sort()) {
    if (!isPnlAccount(code)) continue;
    const t = yearMovement.get(code) as Pair;
    const net = t.debit - t.credit;
    if (net === 0) continue;
    profit -= net;
    lines.push({
      accountCode: code,
      debitMinor: net < 0 ? -net : 0,
      creditMinor: net > 0 ? net : 0,
      dimensions: { ...dimensions },
    });
  }
  if (lines.length === 0) return { lines, netResultMinor: 0 };
  lines.push({
    accountCode: RETAINED_EARNINGS_ACCOUNT,
    debitMinor: profit < 0 ? -profit : 0,
    creditMinor: profit > 0 ? profit : 0,
    dimensions: { ...dimensions },
  });
  const kept = lines.filter((l) => l.debitMinor > 0 || l.creditMinor > 0);
  if (kept.length < 2) return { lines: [], netResultMinor: 0 };
  assertBalanced(kept);
  return { lines: kept, netResultMinor: profit };
}

/** Lines summed per account: what a set of journals moved, as stored on the fiscal year. */
export function netByAccount(
  lines: readonly Pick<JournalLineDraft, 'accountCode' | 'debitMinor' | 'creditMinor'>[],
): Record<string, Pair> {
  const out: Record<string, Pair> = {};
  for (const l of lines) {
    const have = out[l.accountCode] ?? { debit: 0, credit: 0 };
    have.debit += l.debitMinor;
    have.credit += l.creditMinor;
    out[l.accountCode] = have;
  }
  return out;
}

export interface YearChecklistInput {
  readonly entityId: string;
  readonly year: number;
  /** The entity's business date. */
  readonly today: string;
  /** Months of the year that are not closed. */
  readonly monthsNotClosed: readonly string[];
  /** Months of the year with a journal the ledger has not applied yet. */
  readonly monthsWithUnapplied: readonly string[];
  readonly trialBalance: { readonly debitMinor: number; readonly creditMinor: number };
  readonly truncated: boolean;
}

export interface YearChecklistRow {
  readonly key:
    'year_ended' | 'months_closed' | 'unapplied_journals' | 'trial_balance' | 'data_complete';
  readonly label: string;
  readonly status: 'pass' | 'fail';
  readonly detail: string;
  readonly open: string[];
}

/**
 * Before a year is closed (06 §6.4): the year is over, all twelve months are closed (so each month's
 * own checklist passed, including its FX revaluation), the ledger has applied every journal of the
 * year, and the trial balance balances.
 */
export function buildYearChecklist(input: YearChecklistInput): YearChecklistRow[] {
  const ended = input.today > `${input.year}-12-31`;
  const balanced = input.trialBalance.debitMinor === input.trialBalance.creditMinor;
  const rows: YearChecklistRow[] = [
    {
      key: 'year_ended',
      label: 'The year is over',
      status: ended ? 'pass' : 'fail',
      detail: ended
        ? `${input.year} ended on ${input.year}-12-31.`
        : `${input.year} ends on ${input.year}-12-31; a year cannot be closed before it is over.`,
      open: [],
    },
    {
      key: 'months_closed',
      label: 'Every month of the year is closed',
      status: input.monthsNotClosed.length === 0 ? 'pass' : 'fail',
      detail:
        input.monthsNotClosed.length === 0
          ? 'All twelve months are closed.'
          : `${input.monthsNotClosed.length} month(s) are not closed yet. Close them on Period close first.`,
      open: [...input.monthsNotClosed],
    },
    {
      key: 'unapplied_journals',
      label: 'No journal waits to be added to the ledger',
      status: input.monthsWithUnapplied.length === 0 ? 'pass' : 'fail',
      detail:
        input.monthsWithUnapplied.length === 0
          ? 'Every journal of the year has been applied.'
          : 'Some journals have not been applied to the ledger yet. Try again in a minute.',
      open: [...input.monthsWithUnapplied],
    },
    {
      key: 'trial_balance',
      label: 'The trial balance balances',
      status: balanced ? 'pass' : 'fail',
      detail: balanced
        ? `Debits and credits are both ${input.trialBalance.debitMinor}.`
        : `Debits ${input.trialBalance.debitMinor} and credits ${input.trialBalance.creditMinor} differ.`,
      open: [],
    },
  ];
  if (input.truncated) {
    rows.push({
      key: 'data_complete',
      label: 'The check read the whole year',
      status: 'fail',
      detail: 'The ledger of the year is larger than one check reads, so it cannot vouch for it.',
      open: [],
    });
  }
  return rows;
}

/** The twelve months of a year, `YYYY-01` … `YYYY-12`. */
export const monthsOfYear = (year: number): string[] =>
  Array.from({ length: 12 }, (_, i) => `${year}-${String(i + 1).padStart(2, '0')}`);

// --------------------------------------------------------------- revaluation

export interface EntityKind {
  readonly entityId: string;
  readonly type: 'origin' | 'destination';
  readonly currency: string;
}

/**
 * The currency a balance between two countries is owed in (14 §14.9): the advance and the recharge
 * invoice are both in the destination's currency, so the origin's 1200 and 2100 with a destination
 * are foreign-currency balances and the destination's are not. Two countries of the same kind have
 * no denomination rule (nothing posts between them today): undefined, and they are not revalued.
 */
export function intercompanyDenomination(a: EntityKind, b: EntityKind): string | undefined {
  if (a.type === b.type) return undefined;
  return a.type === 'destination' ? a.currency : b.currency;
}

/**
 * What an entity restates of its balances with another country at month end, and in which
 * currency: undefined when nothing is foreign. Between an origin and a destination everything is
 * owed in the destination's currency (`intercompanyDenomination`), so both 1200 and 2100 of the
 * origin are restated. Two countries of the same kind meet only through general transfers
 * (`intercompanyTransfer`), which are owed in the lender's currency: our 1200 (what we lent) is in
 * our own currency and stays at book, our 2100 (what the other lent us) is in theirs and is restated.
 */
export function revaluationBasis(
  self: EntityKind,
  other: EntityKind,
): { currency: string; restate: 'both' | 'payable' } | undefined {
  const denomination = intercompanyDenomination(self, other);
  if (denomination !== undefined) {
    return denomination === self.currency ? undefined : { currency: denomination, restate: 'both' };
  }
  if (self.currency === other.currency) return undefined;
  return { currency: other.currency, restate: 'payable' };
}

export interface RevaluationItem {
  readonly counterEntityId: string;
  /**
   * `payable`: only the 2100 is owed in `currency` (a loan from a country of the same kind); the
   * 1200 is in the entity's own currency and keeps its book value. Default `both`.
   */
  readonly restate?: 'both' | 'payable';
  /** The currency the balance is owed in (the counter-entity's). */
  readonly currency: string;
  /** Closing rate: 1 unit of `currency` buys `rate` of the entity's currency. */
  readonly rate: string;
  readonly rateDate: string;
  /** 1200 with the counter-entity at the period end, in the entity's books (debit − credit). */
  readonly bookReceivableMinor: number;
  /** 2100 with the counter-entity at the period end (credit − debit). */
  readonly bookPayableMinor: number;
  /** The same receivable in the currency it is owed in: the counter-entity's payable to us. */
  readonly foreignReceivableMinor: number;
  /** The same payable in its currency: the counter-entity's receivable from us. */
  readonly foreignPayableMinor: number;
}

export interface RevaluationRow extends RevaluationItem {
  readonly targetReceivableMinor: number;
  readonly targetPayableMinor: number;
  /** Target − book: what the journal moves on 1200 (positive is a debit). */
  readonly receivableAdjustMinor: number;
  /** Target − book: what it moves on 2100 (positive is a credit, the payable grows). */
  readonly payableAdjustMinor: number;
  /** Unrealised gain (positive) or loss on this pair. */
  readonly gainMinor: number;
}

function translate(amountMinor: number, from: string, to: string, rate: string, date: string) {
  if (!isCurrencyCode(from) || !isCurrencyCode(to)) {
    throw new AccountingError(`Unknown currency ${from} or ${to}`);
  }
  return convert(money(amountMinor, from as CurrencyCode), {
    from: from as CurrencyCode,
    to: to as CurrencyCode,
    rate,
    date,
  }).amountMinor;
}

/**
 * The month-end FX revaluation (`revalueFx`, 06 §6.3, §6.5): each foreign-currency balance is
 * restated at the closing rate. The receivable (1200) and the payable (2100) with each other country
 * are restated apart, so the consolidation still matches them pair by pair; the net difference is an
 * unrealised gain or loss on 5200, tagged `fx: unrealised`. Target − book, so running it twice in a
 * month only posts what changed in between. The journal is reversed on the first day of the next month.
 */
export function revaluationPlan(
  entityCurrency: string,
  items: readonly RevaluationItem[],
  dimensions: Dimensions,
): { rows: RevaluationRow[]; lines: JournalLineDraft[]; gainMinor: number } {
  const rows: RevaluationRow[] = items.map((i) => {
    if (i.currency === entityCurrency) {
      throw new AccountingError(
        `${i.counterEntityId} is owed in ${i.currency}: nothing to revalue`,
      );
    }
    const targetReceivableMinor =
      i.restate === 'payable'
        ? i.bookReceivableMinor
        : translate(i.foreignReceivableMinor, i.currency, entityCurrency, i.rate, i.rateDate);
    const targetPayableMinor = translate(
      i.foreignPayableMinor,
      i.currency,
      entityCurrency,
      i.rate,
      i.rateDate,
    );
    const receivableAdjustMinor = targetReceivableMinor - i.bookReceivableMinor;
    const payableAdjustMinor = targetPayableMinor - i.bookPayableMinor;
    return {
      ...i,
      targetReceivableMinor,
      targetPayableMinor,
      receivableAdjustMinor,
      payableAdjustMinor,
      gainMinor: receivableAdjustMinor - payableAdjustMinor,
    };
  });
  const lines: JournalLineDraft[] = [];
  for (const r of rows) {
    const dims = { ...dimensions, counterEntityId: r.counterEntityId, fx: 'unrealised' };
    const rec = r.receivableAdjustMinor;
    if (rec !== 0) {
      lines.push({
        accountCode: '1200',
        debitMinor: rec > 0 ? rec : 0,
        creditMinor: rec < 0 ? -rec : 0,
        dimensions: dims,
      });
    }
    const pay = r.payableAdjustMinor;
    if (pay !== 0) {
      lines.push({
        accountCode: '2100',
        debitMinor: pay < 0 ? -pay : 0,
        creditMinor: pay > 0 ? pay : 0,
        dimensions: dims,
      });
    }
  }
  const gainMinor = rows.reduce((s, r) => s + r.gainMinor, 0);
  if (lines.length > 0 && gainMinor !== 0) {
    lines.push({
      accountCode: '5200',
      debitMinor: gainMinor < 0 ? -gainMinor : 0,
      creditMinor: gainMinor > 0 ? gainMinor : 0,
      dimensions: { ...dimensions, fx: 'unrealised' },
    });
  }
  if (lines.length > 0) assertBalanced(lines);
  return { rows, lines, gainMinor };
}

/** The account's name, for screens that list codes. */
export const accountName = (code: string): string => getAccount(code)?.name ?? `Account ${code}`;
